import React, { useEffect, useState, useMemo } from 'react';
import './classroom.css';
import { config } from '../../config/client';
import authHeader from '../../services/auth-header';

const Classroom = () => {
    const [pending, setPending] = useState([]);
    const [enrolled, setEnrolled] = useState([]);
    
    // Search states
    const [pendingSearch, setPendingSearch] = useState('');
    const [enrolledSearch, setEnrolledSearch] = useState('');
    
    // Sort states
    const [pendingSort, setPendingSort] = useState({ field: '', direction: 'asc' });
    const [enrolledSort, setEnrolledSort] = useState({ field: '', direction: 'asc' });

    useEffect(() => {
        fetch(config.endpoint + '/classroom/manage', {
            method: 'GET',
            headers: authHeader(),
        })
            .then(res => res.json())
            .then(data => {
                // Expecting array of objects: { name, email, enrollment_time, last_accessed, user_id }
                setPending(data.pending_requests || []);
                setEnrolled(data.enrolled_students || []);
            });
    }, []);

    // Sorting function
    const sortData = (data, sortConfig) => {
        if (!sortConfig.field) return data;
        
        return [...data].sort((a, b) => {
            let aValue = a[sortConfig.field] || '';
            let bValue = b[sortConfig.field] || '';
            
            // Handle dates
            if (sortConfig.field === 'enrollment_time' || sortConfig.field === 'last_accessed') {
                aValue = new Date(aValue || 0);
                bValue = new Date(bValue || 0);
            } else {
                // Handle strings (case insensitive)
                aValue = aValue.toString().toLowerCase();
                bValue = bValue.toString().toLowerCase();
            }
            
            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    // Filter function for search
    const filterBySearch = (data, searchTerm) => {
        if (!searchTerm) return data;
        
        return data.filter(student => 
            (student.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (student.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (student.username || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    // Handle sort click
    const handleSort = (field, tableType) => {
        const currentSort = tableType === 'pending' ? pendingSort : enrolledSort;
        const newDirection = currentSort.field === field && currentSort.direction === 'asc' ? 'desc' : 'asc';
        
        if (tableType === 'pending') {
            setPendingSort({ field, direction: newDirection });
        } else {
            setEnrolledSort({ field, direction: newDirection });
        }
    };

    // Get sort icon
    const getSortIcon = (field, tableType) => {
        const currentSort = tableType === 'pending' ? pendingSort : enrolledSort;
        if (currentSort.field !== field) return '↕️';
        return currentSort.direction === 'asc' ? '↑' : '↓';
    };

    // Memoized filtered and sorted data
    const filteredPending = useMemo(() => {
        const filtered = filterBySearch(pending, pendingSearch);
        return sortData(filtered, pendingSort);
    }, [pending, pendingSearch, pendingSort]);

    const filteredEnrolled = useMemo(() => {
        const filtered = filterBySearch(enrolled, enrolledSearch);
        return sortData(filtered, enrolledSort);
    }, [enrolled, enrolledSearch, enrolledSort]);

    const approveStudent = (studentEmail) => {
        fetch(config.endpoint + '/classroom/approve', {
            method: 'POST',
            headers: { ...authHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ 'student_email': studentEmail })
        })
            .then(res => res.json())
            .then(() => {
                setPending(prev => prev.filter(student => student.email !== studentEmail));
                // You may want to refetch or push the full student object here
            });
    };

    const unenrollStudent = (studentEmail) => {
        fetch(config.endpoint + '/classroom/unenroll', {
            method: 'POST',
            headers: { ...authHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ 'student_email': studentEmail })
        })
            .then(res => res.json())
            .then(() => {
                setEnrolled(prev => prev.filter(student => student.email !== studentEmail));
            });
    };

    return (
        <div className="classroom-container">
            <h2>Classroom Management</h2>
            <div className="table-section">
                <div className="section-header">
                    <h3>Pending Requests</h3>
                    <div className="search-container">
                        <input
                            type="text"
                            placeholder="Search by name, email, or username..."
                            value={pendingSearch}
                            onChange={(e) => setPendingSearch(e.target.value)}
                            className="search-input"
                        />
                        <span className="search-icon">🔍</span>
                    </div>
                </div>
                
                {filteredPending.length === 0 ? (
                    <p>{pendingSearch ? 'No pending requests match your search.' : 'No pending requests.'}</p>
                ) : (
                    <table className="table pending-table">
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('name', 'pending')} className="sortable-header">
                                    Name <span className="sort-icon">{getSortIcon('name', 'pending')}</span>
                                </th>
                                <th onClick={() => handleSort('email', 'pending')} className="sortable-header">
                                    Email <span className="sort-icon">{getSortIcon('email', 'pending')}</span>
                                </th>
                                <th onClick={() => handleSort('last_accessed', 'pending')} className="sortable-header">
                                    Last Accessed <span className="sort-icon">{getSortIcon('last_accessed', 'pending')}</span>
                                </th>
                                <th>Activity</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPending.map(student => (
                                <tr key={student.email}>
                                    <td>{student.name || '-'}</td>
                                    <td>{student.email}</td>
                                    <td>{student.enrollment_time ? new Date(student.enrollment_time).toLocaleString() : '-'}</td>
                                    <td>{student.last_accessed ? new Date(student.last_accessed).toLocaleString() : '-'}</td>
                                    <td>
                                        <button onClick={() => approveStudent(student.email)}>Approve</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            <div className="table-section">
                <div className="section-header">
                    <h3>Enrolled Students</h3>
                    <div className="search-container">
                        <input
                            type="text"
                            placeholder="Search by name, email, or username..."
                            value={enrolledSearch}
                            onChange={(e) => setEnrolledSearch(e.target.value)}
                            className="search-input"
                        />
                        <span className="search-icon">🔍</span>
                    </div>
                </div>
                
                {filteredEnrolled.length === 0 ? (
                    <p>{enrolledSearch ? 'No enrolled students match your search.' : 'No enrolled students.'}</p>
                ) : (
                    <table className="table enrolled-table">
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('name', 'enrolled')} className="sortable-header">
                                    Name <span className="sort-icon">{getSortIcon('name', 'enrolled')}</span>
                                </th>
                                <th onClick={() => handleSort('username', 'enrolled')} className="sortable-header">
                                    Username <span className="sort-icon">{getSortIcon('username', 'enrolled')}</span>
                                </th>
                                <th onClick={() => handleSort('email', 'enrolled')} className="sortable-header">
                                    Email <span className="sort-icon">{getSortIcon('email', 'enrolled')}</span>
                                </th>
                                <th onClick={() => handleSort('last_accessed', 'enrolled')} className="sortable-header">
                                    Last Accessed <span className="sort-icon">{getSortIcon('last_accessed', 'enrolled')}</span>
                                </th>
                                <th>Activity</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredEnrolled.map(student => (
                                <tr key={student.email}>
                                    <td>{student.name || '-'}</td>
                                    <td>{student.username || '-'}</td>
                                    <td>{student.email}</td>
                                    <td>{student.last_accessed ? new Date(student.last_accessed).toLocaleString() : '-'}</td>
                                    <td>
                                        <a href={`/?student=${encodeURIComponent(student.username)}`}>View Activity</a>
                                    </td>
                                    <td>
                                        <button onClick={() => unenrollStudent(student.email)}>Unenroll</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default Classroom;
